<template>
  <div class="sidebar-wrapper">
    <!-- Mobile toggle button -->
    <MobileSidebarToggle
      :isMobile="isMobile"
      :toggleSidebar="toggleSidebar"
      :ariaLabel="t('common.actions.toggleMenu')"
    />

    <!-- Backdrop for mobile -->
    <MobileSidebarBackdrop
      :isMobile="isMobile"
      :isOpen="sidebarClass['sidebar-open']"
      :onClose="closeSidebar"
    />

    <div class="module-sidebar" :style="{ width: `${sidebarWidth}px` }" :class="sidebarClass">
    <!-- HEADER -->
    <div class="space-y-1 pb-2 border-bottom-1" :style="{ borderColor: `hsl(var(--border))` }">

      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold" style="color:hsl(var(--foreground));">
          {{ t('common.entityNames.clients') }}
        </h2>
        <!-- Close button for mobile -->
        <MobileSidebarCloseButton
          :isMobile="isMobile"
          :onClose="closeSidebar"
          :ariaLabel="t('common.actions.close')"
        />
      </div>

      <!-- Search with Sort Dropdown -->
      <div class="p-input-icon-left p-input-icon-right">
        <i class="pi pi-search"></i>
        <InputText
          v-model="groupFilter"
          :placeholder="t('common.actions.search')"
          class="w-full h-10 text-sm rounded-lg"
          style="
            border: 1px solid hsl(var(--input));
            background: hsl(var(--secondary));
            color: hsl(var(--foreground));
            padding-right: 4.5rem !important;
          "
          @input="filterGroups(groupFilter)"
          name="search-groups"
        />
        <div class="sort-controls">
          <Dropdown
            v-model="currentSortMethodValue"
            :options="sortMethods"
            optionLabel="label"
            optionValue="value"
            class="sort-dropdown"
            @change="handleSortMethodSelection"
          >
            <template #value="slotProps">
              <i :class="currentSortMethod.icon" :title="currentSortMethod.label"></i>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-2">
                <i :class="slotProps.option.icon"></i>
                <span>{{ slotProps.option.label }}</span>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>


       <Button
         v-if="canCreateClientGroup"
         :label="t('group.form.create')"
         icon="pi pi-plus-circle"
         class="w-full mt-2"
         @click="handleAddGroup"
       />
    </div>


  <div v-if="canViewGroup">
    <!-- LIST AREA -->
    <div class="space-y-2">

      <!-- Loading spinner -->
      <div v-if="showGroupListLoading" class="flex justify-center py-10">
        <ProgressSpinner />
      </div>

      <!-- Groups List -->
      <Draggable
        v-else
        ref="treeRef"
        v-model="groupTreeData"
        :indent="7"
        :nodeKey="getNodeKey"
        :treeLine="false"
        :statHandler="handleTreeStat"
        @open:node="handleNodeOpen"
        @close:node="handleNodeClose"
        @after-drop="handleTreeAfterGroupDrop"
        :ondragstart="handleGroupTreeNodeDragStart"
      >

        <template #default="{ node, stat }">

          <div
            @click.stop="navigateToClientsWithGroupId(node.originalId)"
            class="cursor-pointer rounded-xl p-2 transition group relative items-center"
            :class="route.params.groupid == node.originalId ? 'selected-card' : 'default-card'"
            @dragenter.prevent
            @dragover.prevent="handleClientTreeNodeDragOver($event, node)"
            @dragleave.prevent="handleClientTreeNodeDragLeave(node)"
            @drop.prevent="handleClientTreeNodeDrop($event, node)"
          >

            <!-- Item Layout -->
            <div class="card-row flex items-start gap-1 relative">
              <!-- LEFT: Icon Column -->
              <div class="flex-shrink-0 item-stretch">
                <i
                  class="pi pi-angle-right cursor-pointer"
                  :style="{
                    transform: stat.open ? 'rotate(90deg)' : 'rotate(0deg)',
                    opacity: node.children.length > 0 ? '1' : '0'
                  }"
                  @click.stop="toggleNodeExpand(stat)"
                ></i>
                <i class="pi pi-server card-icon"></i>
              </div>

              <!-- MIDDLE: Text Content -->
              <div class="flex-1 min-w-0">
                <div class="card-title sidebar-title-wrap">{{ node.name }}</div>
                <div v-if="node.description && node.description.trim()" class="card-description line-clamp-2">
                  {{ node.description }}
                </div>
              </div>

              <!-- RIGHT: Actions Column -->
              <div class="card-actions">
                <span class="card-badge px-2 py-1">
                  {{ getClientCountForGroup(node.originalId) }}
                </span>
                <BaseMenuButton
                  v-if="groupsContextMenuItems(getGroupById(node.originalId)).length > 0"
                  icon="pi pi-ellipsis-v"
                  :items="groupsContextMenuItems(getGroupById(node.originalId))"
                  aria-label="Optionen"
                  text
                  size="small"
                />
              </div>
            </div>

          </div>

        </template>

      </Draggable>

    </div>
  </div>
    <FormDialog
      v-model="showCreateProfileDialog"
      :group-id="selectedGroupId"
      :title="t('group.dataview.createProfile')"
      @saved="handleProfileCreated"
    >
      <ConfigProfile :group-id="selectedGroupId" />
    </FormDialog>

    <AssignProfile
      v-model:isOpen="showAssignProfileDialog"
      :group-id="selectedGroupId"
      :client-id="selectedClientId"
      @profile-assigned="showAssignProfileDialog = false"
    />

    <ActionScheduler
      v-model:isOpen="showActionSchedulerDialog"
      :group-id="actionSchedulerGroupId"
      :client-id="selectedClientId"
    />

    <ConfigFormDialog
      v-if="configGroupId"
      v-model="showConfigDialog"
      :group-id="configGroupId"
      :config-mode="selectedConfigMode"
    />

    <SaveFormDialog
      v-model:visible="showDeleteGroupDialog"
      confirm-type="delete"
      :entity-name="t('common.entityNames.group')"
      :entity-display-name="groupToDelete?.name || ''"
      :delete-loading="deleteGroupLoading"
      @delete="handleDeleteGroup"
      @cancel="showDeleteGroupDialog = false"
    />

    <!-- Resize Handle (desktop only) -->
    <div
      v-if="!isMobile"
      class="resize-handle"
      @mousedown="handleMouseDown"
      :class="{ 'resizing': isResizing }"
    ></div>
  </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed, watchEffect, type Ref } from 'vue';
import { useClientsStore } from "@/stores/clients";
import { storeToRefs } from "pinia";
import { useClientGroupsStore } from "@/stores/client-groups";
import { usePermissionsStore } from '~/stores/permissions';
import { useAuth } from '@/stores/auth';
import { useToast } from "primevue/usetoast";
import { Config_Mode } from '~/types/graphql';
import type { ClientView, ClientGroup, SendActionMutation, SendActionMutationVariables } from '~/types/graphql';
import { useRouter, useRoute } from 'vue-router';
import { Draggable, dragContext } from '@he-tree/vue';
import '@he-tree/vue/style/default.css';
import { useTreeOperations, type TreeNode, type DraggableTree } from '~/composables/treeOperations';
import { useI18n } from 'vue-i18n';
import { GroupsContextMenu } from '~/Common/ContextMenu';
import sendAction from '~/graphql/mutations/sendAction.graphql';
import FormDialog from '~/components/base/FormDialog.vue';
import ConfigProfile from '~/components/module/ConfigProfile.vue';
import AssignProfile from '~/components/base/forms/AssignProfile.vue';
import ActionScheduler from '~/components/base/forms/ActionScheduler.vue';
import ConfigFormDialog from '~/components/base/ConfigFormDialog.vue';
import SaveFormDialog from '~/components/base/dialogs/SaveFormDialog.vue';
import { useConfigStore } from '@/stores/config';
import { useResizableSidebar } from '~/composables/useResizableSidebar';
import MobileSidebarToggle from '~/components/base/MobileSidebarToggle.vue';
import MobileSidebarBackdrop from '~/components/base/MobileSidebarBackdrop.vue';
import MobileSidebarCloseButton from '~/components/base/MobileSidebarCloseButton.vue';

const { t } = useI18n();

// Map nullable GraphQL description to the tree item shape expected by useTreeOperations
type ClientGroupTree = Omit<ClientGroup, 'description'> & { description?: string };

const router = useRouter();
const route = useRoute();
const toast = useToast();

const clientStore = useClientsStore();
const groupStore = useClientGroupsStore();
const authStore = useAuth();
const permissionsStore = usePermissionsStore();
const configStore = useConfigStore();

// Resizable sidebar with mobile support
const {
  sidebarWidth,
  isResizing,
  handleMouseDown,
  isMobile,
  toggleSidebar,
  closeSidebar,
  sidebarClass
} = useResizableSidebar({
  storageKey: 'clientsSidebarWidth',
  minWidth: 250,
  maxWidth: 600,
  defaultWidth: 280,
});

const { clients } = storeToRefs(clientStore);
const { groups, listLoading: groupListLoading } = storeToRefs(groupStore);
const { user } = storeToRefs(authStore);

const treeRef = ref<DraggableTree | null>(null);
const treeContainerRef = ref(null);

// Get tree operations from the composable with ClientGroup type parameter
const {
  currentSortMethod,
  currentSortMethodValue,
  sortDirection,
  customSortApplied,
  toggleSortDirection,
  updateTreeData,
  filterItemsHelper,
  filterItemsByIds,
  sortMethods,
  setupTreeDataWatcher
} = useTreeOperations<ClientGroupTree>();

// Status for Tree Data
const groupTreeData = ref<TreeNode[]>([]);
const groupFilter = ref<string>('');
const allGroupsExpanded = ref(true);
const currentGroup = ref<ClientGroup | null>(null);
const showConfigDialog = ref(false);
const configGroupId = ref<number | null>(null);
const selectedConfigMode = ref<Config_Mode>(Config_Mode.Production);
const selectedGroupId = ref<number | null>(null);
const actionSchedulerGroupId = ref<number | null>(null);
const showActionSchedulerDialog = ref(false);
const showCreateProfileDialog = ref(false);
const showAssignProfileDialog = ref(false);
const selectedClientId = ref<number | null>(null);
const showDeleteGroupDialog = ref(false);
const groupToDelete = ref<ClientGroup | null>(null);
const deleteGroupLoading = ref(false);

// Persisted expand/collapse state for group nodes (per user)
const expandedGroupIds = ref<Set<number>>(new Set());
const hasPersistedExpandedState = ref(false);

// Tracking dragged over node
const draggedOverNodeId = ref<number | null>(null);

const { mutate } =
  useMutation<SendActionMutation, SendActionMutationVariables>(sendAction);

// Flag for active dragging: 'tree' or 'client' or null
const activeDragType = ref<'tree' | 'client' | null>(null);

// Tracking drag over state for search field
const isSearchDragOver = ref(false);
const isValidDragData = ref(true);

const messageClient = reactive<{ client?: ClientView; message?: string }>({});

const canCreateClientGroup = computed(() => permissionsStore.canCreateClientGroup());

// Only show groups the user can read or write
const visibleGroups = computed(() => {
  if (!groups.value) return [];
  return groups.value.filter(
    group =>
      permissionsStore.canReadClientGroup(group.name) ||
      permissionsStore.canWrite('clientGroup', group.name)
  );
});

// For global menu visibility, keep the old logic if needed
const canViewGroup = computed(() => visibleGroups.value.length > 0);

const showGroupListLoading = computed(
  () => groupListLoading.value && (!groups.value || groups.value.length === 0),
);


// State var for tracking client dragging
const isDraggingClient = ref(false);


// Load groups on mount
onMounted(() => {
  loadSortSettings();
  loadExpandedState();

  if (user.value?.id) {
    permissionsStore.fetchPermissions(user.value.id);
  }

  loadInitialData();
  window.addEventListener('client-drag-start', onClientDragStart);
  window.addEventListener('client-drag-end', onClientDragEnd);

  // Setup watcher for groups store changes
  setupTreeDataWatcher(
    groupStore,
    groups as Ref<ClientGroupTree[] | null>,
    groupTreeData
  );
});

watchEffect(async () => {
  if (user.value?.id) {
    await permissionsStore.fetchPermissions(user.value.id);
  }
});

watch(() => user.value?.id, () => {
  syncExpandedStateWithGroups();
});

watch(groups, () => {
  syncExpandedStateWithGroups();
});

async function openConfigDialogForGroup(
  groupId: number,
  configMode: Config_Mode = Config_Mode.Production,
) {
   const res = await configStore.getConfigFile(groupId, configMode);
   if (
     !res?.getConfigFile ||
     !res.getConfigFile.structureId ||
     !res.getConfigFile.configId
   ) {
     toast.add({
       severity: 'error',
       summary: t('config.toast.loadError'),
       detail: t('config.toast.noConfigFile'),
       life: 3000,
     });
     return;
   }

   // Check if config is currently locked before opening dialog
   try {
     const isAvailable = await configStore.checkConfigLockStatus(res.getConfigFile.configId);
     if (!isAvailable) {
       toast.add({
         severity: 'warn',
         summary: t('config.toast.configLocked'),
         detail: t('config.lockedDialog.message'),
         life: 5000,
       });
       return;
     }
   } catch {
     // Keep UI behavior consistent: user only sees the lock information toast.
     toast.add({
       severity: 'warn',
       summary: t('config.toast.configLocked'),
       detail: t('config.lockedDialog.message'),
       life: 5000,
     });
     return;
   }

   selectedConfigMode.value = configMode;
   configGroupId.value = groupId;
   showConfigDialog.value = true;
 }

async function handleSendGroupAction(
  action: string,
  id: number,
  message: string = '',
  mode?: string,
  configMode: Config_Mode = Config_Mode.Production,
) {
   try {
     if (action === 'editGroupConfig') {
       // openConfigDialogForGroup handles all errors internally
        await openConfigDialogForGroup(id, configMode);
       return;
     }

    if (action === 'createProfile') {
      selectedGroupId.value = id;
      showCreateProfileDialog.value = true;
      return;
    }

    if (action === 'assignProfile') {
      selectedGroupId.value = id;
      showAssignProfileDialog.value = true;
      return;
    }

    if (action === 'actionScheduler') {
      actionSchedulerGroupId.value = id;
      showActionSchedulerDialog.value = true;
      return;
    }

    if (action === 'deleteGroup') {
      // Confirmation already shown by MenuButton - proceed directly with deletion
      groupToDelete.value = getGroupById(id);
      await handleDeleteGroup();
      return;
    }

     if (action === 'promoteStagingConfig') {
       await groupStore.promoteStagingConfigToProduction(id);

       await groupStore.getGroups();

       toast.add({
         severity: 'success',
         summary: t('common.toast.success'),
         detail: t('group.toast.stagingPromoted'),
         life: 3000,
       });

       return;
     }

    const sendActionResult = await mutate({
      id,
      action,
      message,
    });

    if (sendActionResult) {
      toast.add({
        severity: "success",
        summary: t('common.toast.success'),
        detail: t('group.toast.actionSent'),
        life: 2000,
      });
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('group.toast.actionError'),
      life: 3000,
    });
  }
}

async function handleSendClientAction(
  action: string,
  id: number,
  message: string = '',
) {
  try {
    if (action === 'assignProfile') {
      showAssignProfileDialog.value = true;
      selectedClientId.value = id;
      return;
    }
    if (action === 'actionScheduler') {
      showActionSchedulerDialog.value = true;
      selectedClientId.value = id;
      return;
    }

    const sendActionResult = await mutate({
      id,
      action,
      message,
    });
    if (sendActionResult) {
      toast.add({
        severity: "success",
        summary: t('common.toast.success'),
        detail: t('client.toast.actionSent'),
        life: 2000,
      });
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('client.toast.actionError'),
      life: 3000,
    });
  }
}

const showMessageDialog = ref(false);
const confirmationDialog = reactive<{
  visible: boolean;
  client?: ClientView;
  callback?: () => void;
}>({
  visible: false,
});

function sendMessage(client: ClientView) {
  messageClient.client = client;
  messageClient.message = '';
  showMessageDialog.value = true;
}

function confirmation(method: string, client: ClientView) {
  confirmationDialog.visible = true;
  confirmationDialog.client = client;
  confirmationDialog.callback = () => handleSendClientAction(method, client.id);
}


async function handleDeleteGroup() {
  if (!groupToDelete.value) {
    console.error('[handleDeleteGroup] No group to delete');
    return;
  }

  console.log('[handleDeleteGroup] Deleting group:', groupToDelete.value.id, groupToDelete.value.name);
  deleteGroupLoading.value = true;
  showDeleteGroupDialog.value = false;

  try {
    console.log('[handleDeleteGroup] Calling groupStore.deleteGroup...');
    const result = await groupStore.deleteGroup(groupToDelete.value.id);
    console.log('[handleDeleteGroup] Delete result:', result);

    toast.add({
      severity: "success",
      summary: t('common.toast.success'),
      detail: t('group.toast.deleted'),
      life: 3000,
    });

    console.log('[handleDeleteGroup] Navigating to clients page...');
    await router.push({ name: 'clients' });
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: error?.message || t('group.toast.deleteError'),
      life: 5000,
    });
  } finally {
    deleteGroupLoading.value = false;
    groupToDelete.value = null;
  }
}

function getGroupById(id: number): ClientGroup {
  return groups.value?.find(g => g.id === id) as ClientGroup;
}

const groupsContextMenuItems = GroupsContextMenu(
  router,
  handleSendGroupAction,
  sendMessage,
  confirmation,
);

onUnmounted(() => {
  window.removeEventListener('client-drag-start', onClientDragStart);
  window.removeEventListener('client-drag-end', onClientDragEnd);
});

// Event handlers for client drag events
function onClientDragStart() {
  isDraggingClient.value = true;
  activeDragType.value = 'client';
}

function onClientDragEnd() {
  isDraggingClient.value = false;
  draggedOverNodeId.value = null;
  activeDragType.value = null;
}

// Watch for changes in sort method and direction
watch([currentSortMethodValue, sortDirection], () => {
  if (currentSortMethodValue.value !== 'custom') {
    customSortApplied.value = false;
  }
  persistSortSettings();
  refreshTreeData();
}, { immediate: false });

/**
 * Handle sort method selection from dropdown
 */
function handleSortMethodSelection() {
  if (currentSortMethodValue.value !== 'custom') {
    customSortApplied.value = false;
  }
  persistSortSettings();
  refreshTreeData();
}



/**
 * Handle the toggle of sort direction
 */
function handleToggleSortDirection() {
  toggleSortDirection();
  persistSortSettings();
  refreshTreeData();
}

/**
 * Get the storage key for sort settings (user-specific)
 */
function getSortSettingsStorageKey() {
  return user.value?.id
    ? `groupSortSettings:${user.value.id}`
    : 'groupSortSettings';
}

/**
 * Save sort settings to localStorage (user-specific)
 */
function persistSortSettings() {
  try {
    localStorage.setItem(getSortSettingsStorageKey(), JSON.stringify({
      method: currentSortMethodValue.value,
      direction: sortDirection.value,
      customApplied: customSortApplied.value
    }));
  } catch (error) {
    console.error('Failed to persist sort settings:', error);
  }
}

/**
 * Load sort settings from localStorage (user-specific)
 */
function loadSortSettings() {
  try {
    const saved = localStorage.getItem(getSortSettingsStorageKey());
    if (saved) {
      const settings = JSON.parse(saved);
      currentSortMethodValue.value = settings.method || 'alphabetically';
      sortDirection.value = settings.direction || 'asc';
      customSortApplied.value = settings.customApplied || false;
    }
  } catch (error) {
    console.error('Failed to load sort settings:', error);
  }
}

/**
 * Get the storage key for custom tree order (user-specific)
 */
function getTreeOrderStorageKey() {
  return user.value?.id
    ? `groupTreeOrder:${user.value.id}`
    : 'groupTreeOrder';
}

function countTreeNodes(nodes: TreeNode[]): number {
  return nodes.reduce((count, node) => count + 1 + countTreeNodes(node.children || []), 0);
}

function dedupeTreeNodes(nodes: TreeNode[], seen = new Set<number>()): TreeNode[] {
  return nodes
    .filter((node) => {
      if (!Number.isFinite(node.originalId)) {
        return false;
      }
      if (seen.has(node.originalId)) {
        return false;
      }
      seen.add(node.originalId);
      return true;
    })
    .map((node) => ({
      ...node,
      children: dedupeTreeNodes(node.children || [], seen),
    }));
}

/**
 * Load custom tree order from localStorage
 */
function loadCustomTreeOrder(): TreeNode[] | null {
  try {
    const saved = localStorage.getItem(getTreeOrderStorageKey());
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) {
        return null;
      }
      return dedupeTreeNodes(parsed as TreeNode[]);
    }
  } catch (error) {
    console.error('Failed to load custom tree order:', error);
  }
  return null;
}

function getExpandedStateStorageKey() {
  return user.value?.id
    ? `groupExpandedState:${user.value.id}`
    : 'groupExpandedState';
}

function loadExpandedState() {
  try {
    const saved = localStorage.getItem(getExpandedStateStorageKey());
    if (saved) {
      const parsed = JSON.parse(saved) as number[];
      expandedGroupIds.value = new Set(parsed.filter(id => Number.isFinite(id)));
      hasPersistedExpandedState.value = true;
    } else {
      expandedGroupIds.value = new Set();
      hasPersistedExpandedState.value = false;
    }
  } catch (error) {
    console.error('Failed to load expanded group state:', error);
    expandedGroupIds.value = new Set();
    hasPersistedExpandedState.value = false;
  }
}

function persistExpandedState() {
  try {
    localStorage.setItem(
      getExpandedStateStorageKey(),
      JSON.stringify(Array.from(expandedGroupIds.value))
    );
    hasPersistedExpandedState.value = true;
  } catch (error) {
    console.error('Failed to persist expanded group state:', error);
  }
}

function initializeExpandedStateIfNeeded() {
  if (hasPersistedExpandedState.value || !groups.value?.length) {
    return;
  }
  expandedGroupIds.value = new Set(
    groups.value
      .map(group => group.id)
      .filter((id): id is number => typeof id === 'number')
  );
  hasPersistedExpandedState.value = true;
}

function syncExpandedStateWithGroups() {
  if (!groups.value) return;
  const validIds = new Set(groups.value.map(group => group.id));
  const next = new Set<number>();

  expandedGroupIds.value.forEach(id => {
    if (validIds.has(id)) {
      next.add(id);
    }
  });

  expandedGroupIds.value = next;
  if (hasPersistedExpandedState.value) {
    persistExpandedState();
  }
}

function handleTreeStat(stat: any) {
  const nodeId = stat?.data?.originalId;
  if (nodeId && hasPersistedExpandedState.value) {
    stat.open = expandedGroupIds.value.has(nodeId);
  }
  return stat;
}

function handleNodeOpen(stat: any) {
  const nodeId = stat?.data?.originalId;
  if (nodeId) {
    initializeExpandedStateIfNeeded();
    expandedGroupIds.value.add(nodeId);
    persistExpandedState();
  }
}

function handleNodeClose(stat: any) {
  const nodeId = stat?.data?.originalId;
  if (nodeId) {
    initializeExpandedStateIfNeeded();
    expandedGroupIds.value.delete(nodeId);
    persistExpandedState();
  }
}

/**
 * Load initial data (groups and clients)
 */
async function loadInitialData() {
  if (!groups.value?.length && !groupListLoading.value) {
    await groupStore.getGroups();
  }
  if (!clients.value?.length && !clientStore.listLoading) {
    await clientStore.getClients();
  }
  refreshTreeData();
}
/**
 * Refresh the tree data based on current settings
 */
function refreshTreeData() {
  if (groupFilter.value.trim()) {
    filterGroups(groupFilter.value);
  } else {
    // If custom sort is applied, try to load the saved order
    if (customSortApplied.value && currentSortMethodValue.value === 'custom') {
      const savedOrder = loadCustomTreeOrder();
      if (savedOrder) {
        // Validate that the saved order contains valid group IDs
        const currentGroupIds = new Set(groups.value?.map(g => g.id) || []);
        const savedGroupIds = new Set<number>();

        const collectIds = (nodes: TreeNode[]) => {
          nodes.forEach(node => {
            savedGroupIds.add(node.originalId);
            if (node.children?.length) {
              collectIds(node.children);
            }
          });
        };
        collectIds(savedOrder);
        const savedNodeCount = countTreeNodes(savedOrder);

        // If saved IDs match current groups and there are no duplicate IDs, use saved order
        if (savedGroupIds.size === currentGroupIds.size &&
            savedNodeCount === savedGroupIds.size &&
            Array.from(savedGroupIds).every(id => currentGroupIds.has(id))) {
          groupTreeData.value = savedOrder;
          return;
        }
      }
    }

    // Otherwise, regenerate tree with current sort method
    groupTreeData.value = updateTreeData(
      groups.value as ClientGroupTree[],
      currentSortMethodValue.value,
      sortDirection.value,
      customSortApplied.value
    );
  }
}

function filterGroups(filterText: string) {
  const trimmedFilter = filterText.trim().toLowerCase();

  if (!trimmedFilter) {
    refreshTreeData();
    return;
  }

  if (visibleGroups.value) {
    groupTreeData.value = filterItemsHelper(trimmedFilter, visibleGroups.value as ClientGroupTree[]);
  }
}

/**
 * Handle add group button click
 */
const handleAddGroup = () => {
  router.push({ name: "clientGroups-add" });
};

/**
 * Handle drag start for tree nodes
 */
function handleGroupTreeNodeDragStart(event: DragEvent) {
  activeDragType.value = 'tree';
}

/**
 * Handles the after drop event for the tree.
 * This function is responsible for moving the group to the new parent group.
 */
async function handleTreeAfterGroupDrop() {
  activeDragType.value = null;
  isSearchDragOver.value = false;

  // Check if the dragContext is valid and contains the necessary information
  if (!dragContext.dragNode || !dragContext.startInfo || !dragContext.targetInfo) {
    return;
  }

  const dragNode = dragContext.dragNode;
  if (!dragNode) {
    console.error("Drag node is null");
    return;
  }

  const sourceGroupId = dragNode.data.originalId;
  const startParent = dragContext.startInfo.parent ? dragContext.startInfo.parent.data.originalId : null;
  const targetParent = dragContext.targetInfo.parent ? dragContext.targetInfo.parent.data.originalId : null;

  // All changes to the tree are custom, even if the original sort method was upheld
  currentSortMethodValue.value = 'custom';
  customSortApplied.value = true;

  // Persist the custom flag immediately
  persistSortSettings();

  // If parent is unchanged, just reorder in frontend
  // NOTE: The he-tree library has ALREADY updated groupTreeData.value with the new order
  // at this point (@after-drop fires after the visual update), so we just need to persist it
  if (startParent === targetParent) {
    // Just persist the current state - the tree is already reordered
    try {
      const dedupedTree = dedupeTreeNodes(groupTreeData.value);
      groupTreeData.value = dedupedTree;
      localStorage.setItem(getTreeOrderStorageKey(), JSON.stringify(dedupedTree));
    } catch (error) {
      console.error('Failed to persist custom tree order:', error);
    }

    toast.add({
      severity: "success",
      summary: t('common.toast.success'),
      detail: t('group.toast.reordered') || 'Reihenfolge geändert',
      life: 2000,
    });
    return;
  }

  // If parent changed, do the move as before
  try {
    const response = await groupStore.moveGroup(sourceGroupId, targetParent);
    if (response && !response.errors) {
      toast.add({
        severity: "success",
        summary: t('common.toast.success'),
        detail: t('group.toast.moved'),
        life: 2000,
      });
    } else {
      let backendError = "Verschieben fehlgeschlagen";
      if (response && response.errors && response.errors.length > 0) {
        backendError = response.errors.map(e => e.message).join(' | ') || backendError;
      }
      console.error("Move group failed. Full response:", response);
      throw new Error(backendError);
    }
  } catch (error: any) {
    // Log full error details for debugging
    console.error("Fehler beim Verschieben der Gruppe:", error);

    // Extract user-friendly error message
    let errorDetail = t('group.toast.moveError');

    // Check for specific error messages
    const errorMessage = error?.message || '';
    const graphQLErrors = error?.graphQLErrors || [];

    // Check for "Cannot move temporary groups" error
    if (errorMessage.includes('Cannot move temp groups') ||
        graphQLErrors.some((e: any) => e.message?.includes('Cannot move temp groups'))) {
      errorDetail = t('group.toast.cannotMoveTemp') || 'Temp Gruppen können nicht verschoben werden';
    }
    // Check for other specific errors
    else if (errorMessage.includes('circular dependency') || errorMessage.includes('Zirkelbezug')) {
      errorDetail = t('group.toast.circularDependency') || 'Gruppe kann nicht in eine ihrer Untergruppen verschoben werden';
    }
    else if (errorMessage.includes('permission') || errorMessage.includes('Berechtigung')) {
      errorDetail = t('group.toast.noPermission') || 'Keine Berechtigung zum Verschieben dieser Gruppe';
    }
    // Use backend error message if available and readable
    else if (errorMessage && !errorMessage.includes('ApolloError') && !errorMessage.includes('GraphQL')) {
      errorDetail = errorMessage;
    }
    // Check GraphQL errors for readable messages
    else if (graphQLErrors.length > 0) {
      const readableErrors = graphQLErrors
        .map((e: any) => e.message)
        .filter((msg: string) => msg && !msg.includes('GraphQL') && !msg.includes('Apollo'));

      if (readableErrors.length > 0) {
        errorDetail = readableErrors.join(', ');
      }
    }

    toast.add({
      severity: "error",
      summary: t('common.toast.error'),
      detail: errorDetail,
      life: 4000,
    });

    // Refresh the tree to show the correct state
    await groupStore.getGroups();
    refreshTreeData();
    if (groupFilter.value.trim()) {
      filterGroups(groupFilter.value);
    }
  }
}

/**
 * Handle dragover events on tree nodes.
 * This function prevents the default behavior and sets the dragged over node ID.
 *
 * @param event - The drag event
 * @param node - The node that was dragged over
 */
function handleClientTreeNodeDragOver(event: DragEvent, node: TreeNode) {
  // Ignore the event if it is a tree drag, don't interfere with the tree drag
  // This is critical for the drag over of draggable library to work correctly
  if (activeDragType.value === 'tree') {
    return;
  }
  // Stop propagation for client drag-over events on groups, critical for drop events
  event.stopPropagation();

  if (event.dataTransfer) {
    // Set the drop effect based on the validity of the drag data
    activeDragType.value = 'client';
    draggedOverNodeId.value = node.originalId;
  }
}

/**
 * Handle dragleave events on tree nodes.
 * This function is responsible for removing the highlight from the node.
 *
 * @param node - The node that was dragged over
 */
function handleClientTreeNodeDragLeave(node: TreeNode) {
  // Ignore the event if it is a tree drag
  if (activeDragType.value === 'tree') {
    return;
  }

  if (draggedOverNodeId.value === node.originalId) {
    draggedOverNodeId.value = null;
  }
}

/**
 * Handle drop events on tree nodes.
 * This function is responsible for moving the client to the target group.
 *
 * @param event - The drag event
 * @param node - The target node
 */
async function handleClientTreeNodeDrop(event: DragEvent, node: TreeNode) {
  // Ignore the event if it is a tree drag
  if (activeDragType.value === 'tree') {
    return;
  }

  // Remove the highlight from the dragged over node
  const targetGroupId = node.originalId;
  draggedOverNodeId.value = null;
  activeDragType.value = null;

  if (!event.dataTransfer) {
    console.error("No dataTranfer-Object available");
    return;
  }

  try {
    // Get Data from the drag event
    const clientData = event.dataTransfer.getData('application/json');

    if (!clientData) {
      console.error("No valid JSON data received");
      return;
    }

    const client = JSON.parse(clientData);

    // Check if the client is already in the target group
    if (client.clientGroupId === targetGroupId) {
      toast.add({
        severity: "info",
        summary: t('common.toast.info'),
        detail: t('client.toast.alreadyInGroup'),
        life: 3000,
      });
      return;
    }
    // Move the client to the target group
    const success = await clientStore.moveClient(client.id, targetGroupId);

    if (success) {
      toast.add({
        severity: "success",
        summary: t('common.toast.success'),
        detail: t('client.toast.moved'),
        life: 3000,
      });

      // Update the client data after the move
      await clientStore.getClients();
    } else {
      throw new Error("Client move failed");
    }
  } catch (error) {
    console.error('Error while processing the drop:', error);
    toast.add({
      severity: "error",
      summary: t('common.toast.error'),
      detail: t('client.toast.moveError'),
      life: 3000,
    });
  }
}

/**
 * Gets the key for a node in the tree
 *
 * @param stat - The node's state
 * @param index - The index of the node
 * @return The key for the node
 */
function getNodeKey(stat: any, index: number) {
  return stat.data.originalId || index;
}

/**
 * Navigates to group page with all clients of the group
 *
 * @param groupId - The ID of the group to navigate to
 */
function navigateToClientsWithGroupId(groupId: number) {
  if (!groupId) return;
  router.push({ name: "clientGroups-groupid", params: { groupid: groupId } });
}

/**
 * Toggles the expanded state of a node
 */
function toggleNodeExpand(stat: any) {
  stat.open = !stat.open;

  const nodeId = stat?.data?.originalId;
  if (!nodeId) {
    return;
  }

  initializeExpandedStateIfNeeded();
  if (stat.open) {
    expandedGroupIds.value.add(nodeId);
  } else {
    expandedGroupIds.value.delete(nodeId);
  }
  persistExpandedState();
}

/**
 * Toggles the expanded state of all groups in the tree
 */
function toggleAllGroupsExpanded() {
  if (treeRef.value) {
    if (allGroupsExpanded.value) {
      treeRef.value.closeAll();
      expandedGroupIds.value = new Set();
    } else {
      treeRef.value.openAll();
      expandedGroupIds.value = new Set(
        (groups.value || [])
          .map(group => group.id)
          .filter((id): id is number => typeof id === 'number')
      );
    }
    allGroupsExpanded.value = !allGroupsExpanded.value;
    persistExpandedState();
  }
}

/**
 * Returns the number of clients in a group and its subgroups
 *
 * @param groupId - The ID of the group
 * @return The number of clients in the group and its subgroups
 */
function getClientCountForGroup(groupId: number): number {
  if (!clients.value) return 0;

  // Function to get all subgroup IDs recursively
  const getAllSubgroupIds = (parentId: number): number[] => {
    const subgroups = groups.value?.filter(g => g.parentGroupId === parentId) || [];
    let ids = [parentId];

    for (const subgroup of subgroups) {
      if (subgroup.id) {
        ids = [...ids, ...getAllSubgroupIds(subgroup.id)];
      }
    }

    return ids;
  };

  // Retrieving all subgroup IDs
  const groupIds = getAllSubgroupIds(groupId);

  // Counting clients in the group and its subgroups
  return clients.value.filter(client =>
    client.clientGroupId && groupIds.includes(client.clientGroupId)
  ).length;
}
/**
 * Handle dragover events on the search input
 */
function handleSearchDragOver(event: DragEvent) {
  if (!event.dataTransfer) {
    isSearchDragOver.value = false;
    return;
  }

  // Check if the dragged data is valid
  isValidDragData.value = event.dataTransfer.types.includes('application/json');
  isSearchDragOver.value = true;

  // Set the drop effect based on the validity of the drag data
  event.dataTransfer.dropEffect = isValidDragData.value ? "copy" : "none";
}

function handleProfileCreated() {
  showCreateProfileDialog.value = false;
  toast.add({
    severity: "success",
    summary: t('common.toast.success'),
    detail: t('profile.toast.created'),
    life: 2000,
  });
}

/**
 * Handle dragleave events on the search input
 */
function handleSearchDragLeave() {
  isSearchDragOver.value = false;
}

/**
 * Handle drop events on the search input
 * Extracts the name of the dropped item and uses it as a filter
 */
function handleSearchDrop(event: DragEvent) {
  // Stop propagation and prevent default behavior
  event.stopPropagation();
  event.preventDefault();

  isSearchDragOver.value = false;

  if (!event.dataTransfer) {
    console.error("No dataTransfer-Object available!");
    return;
  }

  // If the drag data is not valid, do nothing
  if (!isValidDragData.value) {
    return;
  }

  let filterText = '';

  try {
    // Only JSON format is accepted
    if (event.dataTransfer.types.includes('application/json')) {
      const data = JSON.parse(event.dataTransfer.getData('application/json'));
      if(data.__typename === 'ClientView') {
        // Add client prefix to the filter text
        filterText = 'client:' + data.hostname || '';
      }
      else if(data.__typename === 'ClientGroup') {
        filterText = data.name || '';
      }
      else {
        console.error("Unknown type:", data.__typename);
      }
    }
    else {
      console.error("Unkown format:", event.dataTransfer.types);
    }
  } catch (error) {
    console.error("Error while processing the drop data:", error);
  }

  if (filterText) {
    filterGroups(filterText);
  }
}
</script>

<style scoped>

.he-tree {
  min-height: 200px;
  padding: 8px;
}

.node-inner {
  border: 1px solid hsl(var(--border));
  border-radius: 4px;
  margin-bottom: 2px;
  background: hsl(var(--card));
  transition: all 0.2s;
  cursor: pointer;
  position: relative;
}

.node-inner:hover {
  background: hsl(var(--card-foreground) / 0.06);
  box-shadow: 0 1px 3px hsl(var(--foreground) / 0.06);
}

.droppable-hover {
  background: hsl(var(--primary) / 0.06);
  border: 2px dashed hsl(var(--primary));
  box-shadow: 0 0 0 2px hsl(var(--primary) / 0.12);
}

.he-tree :deep(.he-tree__placeholder) {
  background: hsl(var(--primary) / 0.08);
  padding: 4px;
}

.he-tree :deep(.he-tree-dragging) {
  opacity: 0.7;
  background: hsl(var(--primary) / 0.08);
  border: 1px solid hsl(var(--primary));
  border-radius: 4px;
}

@keyframes pulse {
  0% { transform: translateY(0); filter: blur(0); }
  50% { transform: translateY(-3px); filter: blur(.5px); }
  100% { transform: translateY(0); filter: blur(0); }
}

.highlight-dropzone {
  transition: all 0.3s ease;
  background: hsl(var(--primary) / 0.04);
  border: 2px dashed hsl(var(--primary));
  border-radius: 4px;
  box-shadow: 0 0 10px hsl(var(--primary) / 0.12);
}

.highlight-dropzone-node {
  transition: all 0.3s ease;
  border: 2px dashed hsl(var(--primary));
  background: hsl(var(--primary) / 0.03);
}

.tree-container.highlight-dropzone {
  animation: pulse 1.5s infinite alternate;
}

.node-inner::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  background: hsl(var(--primary) / 0.04);
  pointer-events: none;
}

.node-inner:hover::after {
  opacity: 1;
}

.p-input-icon-left {
  position: relative;
  display: flex;
  align-items: center;
}

.p-input-icon-right {
  position: relative;
  display: flex;
  align-items: center;
}

.p-input-icon-left .p-inputtext {
  padding-left: 2.2rem !important;
}

.p-input-icon-left i {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: hsl(var(--muted-foreground));
  pointer-events: none;
  font-size: 1rem;
}

.sort-controls {
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.sort-dropdown {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  width: 2rem !important;
  height: 2rem !important;
}

.sort-dropdown :deep(.p-dropdown-trigger) {
  display: none !important;
  width: 0 !important;
}

.sort-dropdown :deep(.p-dropdown-label) {
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.sort-dropdown :deep(.p-dropdown) {
  border: none !important;
  background: transparent !important;
}

.sort-dropdown i {
  color: hsl(var(--muted-foreground));
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.2s;
}

.sort-dropdown:hover i {
  color: hsl(var(--foreground));
}


.sort-direction-icon {
  cursor: pointer;
  padding: 1.3rem;
  border-radius: 50%;
  transition: background-color 0.2s, color 0.2s;
  color: hsl(var(--muted-foreground));
  font-size: 1rem;
}

.sort-direction-icon:hover {
  background: hsl(var(--foreground) / 0.06);
  color: hsl(var(--foreground));
}

button {
  background-color: hsl(var(--primary));
  color: black;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

button:hover {
  background-color: hsl(var(--primary) / 0.9) !important;
  border-color: #ffcc32 !important;
  color: black !important;
}

button:disabled {
  background-color: hsl(var(--primary) / 0.5);
  color: hsl(var(--muted-foreground));
  cursor: not-allowed;
}
.card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: hsl(var(--foreground));
  line-height: 1.2;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible;
}

.card-description {
  font-size: 0.75rem;
  color: hsl(var(--muted-foreground));
  margin-top: 2px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible;
}

.card-icon {
  color: hsl(var(--card-icon));
  font-size: 0.9rem;
  margin-left: 7px;
  line-height: 1;
}

.card-badge {
  background: hsl(var(--secondary));
  color: hsl(var(--foreground));
  border-radius: 20px;
  font-size: 0.7rem;
}

.card-arrow {
  color: hsl(var(--primary));
  font-size: 0.7rem;
  position: absolute;
  top: 12px;
  transform: translate(-50%, 0);
}

.group:hover .card-arrow {
  opacity: 1;
  color: hsl(var(--muted-foreground));}


.default-card {
  background: hsl(var(--card));
  margin-bottom: 5px;
  transition: var(--transition-smooth);
}

.default-card:hover {
  background: hsl(var(--accent));
  border-radius: 6px;

}

.selected-card {
  background: hsl(var(--accent));
  border-radius: 6px;
  box-shadow: 0 2px 6px hsl(var(--shadow-subtle));
  margin-bottom: 5px;
  transition: var(--transition-smooth);
}

.selected-card:hover .card-arrow{
  color: hsl(var(--primary));
}


.line-clamp-2 {
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
  white-space: normal;
}

/* Mobile UI Elements */
/* Styles now in unified components: MobileSidebarToggle, MobileSidebarBackdrop, MobileSidebarCloseButton */


.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.2s;
  z-index: 10;
}

.resize-handle:hover,
.resize-handle.resizing {
  background: hsl(var(--primary) / 0.3);
}

.resize-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 40px;
  background: hsl(var(--border));
  opacity: 0;
  transition: opacity 0.2s;
}

.resize-handle:hover::after,
.resize-handle.resizing::after {
  opacity: 1;
}

/* Wrapper to manage mobile/desktop layout */
.sidebar-wrapper {
  display: contents;
}

@media (max-width: 768px) {
  .sidebar-wrapper {
    display: block;
  }
}

:deep(.p-button) { border: 1px solid transparent; }
:deep(.p-button:hover) { border-color: #ffcc32 !important; }

.module-sidebar {
  container-type: inline-size;
}

/* Progressive font scaling for narrower widths */
@container (max-width: 320px) {
  .card-title {
    font-size: 0.88rem;
  }

  .card-description {
    font-size: 0.72rem;
  }

  .card-icon {
    font-size: 0.85rem;
    margin-left: 5px;
  }

  .card-badge {
    font-size: 0.68rem;
  }

  h2 {
    font-size: 1rem !important;
  }
}

@container (max-width: 290px) {
  .card-title {
    font-size: 0.82rem;
  }

  .card-description {
    font-size: 0.68rem;
  }

  .card-icon {
    font-size: 0.8rem;
    margin-left: 4px;
  }

  .card-badge {
    font-size: 0.65rem;
    padding-inline: 0.35rem;
    padding-block: 0.1rem;
  }

  h2 {
    font-size: 0.95rem !important;
  }

  .card-actions {
    gap: 4px;
  }
}

@container (max-width: 260px) {
  .card-title {
    font-size: 0.78rem;
  }

  .card-description {
    font-size: 0.65rem;
  }

  .card-icon {
    font-size: 0.75rem;
    margin-left: 3px;
  }

  .card-badge {
    font-size: 0.62rem;
    padding-inline: 0.3rem;
    padding-block: 0.05rem;
  }

  h2 {
    font-size: 0.9rem !important;
  }

  .card-actions {
    gap: 3px;
  }

  .p-input-icon-left i {
    font-size: 0.85rem;
  }

  .p-inputtext {
    font-size: 0.8rem !important;
  }
}
.card-row {
  flex-wrap: nowrap;
  align-items: flex-start;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
}
</style>
